import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from './Navbar';
import { Brain, Sparkles, UserPlus, Info, Zap, ArrowRight, Lock } from 'lucide-react';
import HeroCard from '../CardFiles/HeroCard';
import './InviteAcceptance.css';

const INVITE_VARIANTS = [
  "{name} invites you to know yourself.",
  "{name} wants to unlock a new dimension of sync with you.",
  "{name} shared their true persona — now it's your turn.",
  "{name} thinks you're more than who you think you are.",
  "Ready to see how you truly sync with {name}?",
  "{name} found their persona. Can you match their energy?"
];

const getCreativeInvite = (name, code) => {
  if (!name) return "Someone invites you to know yourself.";
  const index = code
    ? code.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % INVITE_VARIANTS.length
    : 0;
  return INVITE_VARIANTS[index].replace("{name}", name);
};

// ── Locked "your card" placeholder ───────────────────────────────────────────
const LockedCard = ({ onClick, inviterName }) => (
  <div className="locked-card-wrapper" onClick={onClick} role="button" tabIndex={0}>
    <div className="locked-card-body">
      {/* Blurred skeleton that echoes a real card's full structure */}
      <div className="locked-blur-content" aria-hidden="true">
        {/* Name + type row */}
        <div className="locked-fake-header">
          <div className="locked-fake-avatar" />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div className="locked-fake-bar" style={{ width: '65%', height: '14px' }} />
            <div className="locked-fake-bar" style={{ width: '42%', height: '10px' }} />
          </div>
        </div>
        {/* Quote bar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '4px 0' }}>
          <div className="locked-fake-bar" style={{ width: '92%', height: '10px' }} />
          <div className="locked-fake-bar" style={{ width: '75%', height: '10px' }} />
        </div>
        {/* Brain balance bar */}
        <div className="locked-fake-bar" style={{ width: '100%', height: '44px', borderRadius: '12px' }} />
        {/* Strengths grid */}
        <div className="locked-fake-grid">
          <div className="locked-fake-bar" style={{ height: '40px', borderRadius: '10px' }} />
          <div className="locked-fake-bar" style={{ height: '40px', borderRadius: '10px' }} />
          <div className="locked-fake-bar" style={{ height: '40px', borderRadius: '10px' }} />
          <div className="locked-fake-bar" style={{ height: '40px', borderRadius: '10px' }} />
        </div>
        {/* Superpower bar */}
        <div className="locked-fake-bar" style={{ width: '100%', height: '36px', borderRadius: '10px' }} />
      </div>

      {/* Lock overlay */}
      <div className="locked-overlay">
        <div className="locked-icon-ring">
          <Lock size={22} color="white" />
        </div>
        <p className="locked-overlay-title">Your Persona Awaits</p>
        <p className="locked-overlay-hint">
          {inviterName
            ? `See how you match with ${inviterName.split(' ')[0]}`
            : 'Discover who you really are'}
        </p>
      </div>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
export const InviteAcceptance = ({
  inviteCode,
  user,
  onSignInClick,
  onStartTest,
  onLinkProfile
}) => {
  const [inviteData, setInviteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStickyBar, setShowStickyBar] = useState(false);
  const mainCtaRef = useRef(null);
  const autoLinked = useRef(false);

  useEffect(() => {
    const validate = async () => {
      if (!inviteCode) { setError('No invite code provided'); setIsLoading(false); return; }
      try {
        const res = await fetch(`/api/invites/validate/${inviteCode}`);
        const data = await res.json();
        if (data.success) setInviteData(data.invite);
        else setError(data.error || 'Invalid invite code');
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    validate();
  }, [inviteCode]);

  // Auto-link immediately when a logged-in user lands on this page.
  // onLinkProfile intentionally omitted from deps — it's recreated every render
  // but we only want this to fire once when inviteData first loads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && inviteData && !isLoading && !autoLinked.current) {
      autoLinked.current = true;
      onLinkProfile(inviteData.inviter_name);
    }
  }, [user, inviteData, isLoading]);

  const parseTestResults = (raw) => {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || Object.keys(parsed).length === 0) return null;
      return parsed;
    } catch { return null; }
  };

  // Show sticky bar only when the main CTA has scrolled out of view
  useEffect(() => {
    const el = mainCtaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inviteData]);

  const handleStartTest = () => {
    sessionStorage.setItem('pendingInviteCode', inviteCode);
    onStartTest();
  };

  const handleSignIn = () => {
    sessionStorage.setItem('pendingInviteCode', inviteCode);
    onSignInClick();
  };

  // ── Loading ──
  if (isLoading) return (
    <div className="invite-acceptance-page base-background">
      <Navbar currentUser={user} onSignInClick={onSignInClick} />
      <div className="ambient-bg">
        <div className="ambient-orb orb-purple" />
        <div className="ambient-orb orb-pink" />
      </div>
      <div className="invite-container">
        <div className="loading-section">
          <div className="button-spinner" />
          <p style={{ marginTop: '20px', color: 'rgba(255,255,255,0.6)' }}>Validating invitation...</p>
        </div>
      </div>
    </div>
  );

  // ── Error ──
  if (error) return (
    <div className="invite-acceptance-page base-background">
      <Navbar currentUser={user} onSignInClick={onSignInClick} />
      <div className="ambient-bg">
        <div className="ambient-orb orb-purple" />
        <div className="ambient-orb orb-pink" />
      </div>
      <div className="invite-container">
        <div className="error-section glass-card">
          <div className="error-icon">⚠️</div>
          <h2>Invitation Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.href = '/'} className="glass-cta">Back to Home</button>
        </div>
      </div>
    </div>
  );

  if (!inviteData) return null;

  // ── Extract inviter card data ──
  const inviterResults = parseTestResults(inviteData.inviter_test_results);
  let pData = null, gData = null, hasInviterCard = false;
  if (inviterResults) {
    const source = inviterResults.cards_data || inviterResults.card_data || inviterResults;
    const pd = source.personality_card || source.personality || source;
    const gd = source.growth_card || source.growth || source;
    if (pd?.persona) { pData = pd; gData = gd; hasInviterCard = true; }
  }

  const inviterFirstName = inviteData.inviter_name?.split(' ')[0] || 'them';

  return (
    <div className="invite-acceptance-page base-background">
      <Navbar currentUser={user} onSignInClick={onSignInClick} />

      <div className="ambient-bg">
        <div className="ambient-orb orb-purple" />
        <div className="ambient-orb orb-pink" />
      </div>

      <div className="invite-container">
        <div className="invite-acceptance-section">

          {/* ── Personal invite badge ── */}
          <div className="invite-from-pill invite-stagger-1">
            <span className="invite-from-dot" />
            <span>{inviteData.inviter_name} invited you personally</span>
          </div>

          {/* ── Hero headline ── */}
          <div className="invite-hero-header invite-stagger-1">
            <h1 className="headline">
              You don't know <span className="headline-accent">you.</span>
            </h1>
            <p className="subheadline">
              Your true persona is waiting to be found.
            </p>
          </div>

          {/* ── Cards: theirs (truncated) vs yours (locked) ── */}
          <div className="cards-reveal-section invite-stagger-2">
            {hasInviterCard && (
              <>
                <div className="card-slot">
                  <div className="card-slot-label their-card-label">
                    <span className="card-slot-dot their-dot" />
                    {inviterFirstName}'s Persona
                  </div>
                  <div className="inviter-card-truncated">
                    <HeroCard personalityData={pData} growthData={gData} />
                    <div className="card-truncate-fade" />
                  </div>
                </div>

                <div className="cards-vs-connector">
                  <div className="vs-line" />
                  <div className="vs-badge">vs</div>
                  <div className="vs-line" />
                </div>
              </>
            )}

            <div className="card-slot">
              <div className="card-slot-label your-card-label">
                <span className="card-slot-dot your-dot" />
                Your Persona
              </div>
              <LockedCard
                onClick={!user ? handleStartTest : undefined}
                inviterName={inviteData.inviter_name}
              />
            </div>
          </div>

          {/* ── CTA section ── */}
          {user ? (
            <div className="viral-cta-section invite-stagger-3">
              <button
                onClick={() => onLinkProfile(inviteData?.inviter_name)}
                className="viral-cta-btn"
              >
                <UserPlus size={18} />
                Connect with {inviterFirstName}
              </button>
              <p className="cta-microcopy">
                Signed in as {user?.fullName || user?.emailAddresses?.[0]?.emailAddress}
              </p>
            </div>
          ) : (
            <div className="viral-cta-section invite-stagger-3">
              {/* Social proof */}
              <div className="social-proof-row">
                <div className="trust-piles">
                  <div className="mini-pile" style={{ background: '#ec4899' }}>SJ</div>
                  <div className="mini-pile" style={{ background: '#8b5cf6' }}>MG</div>
                  <div className="mini-pile" style={{ background: '#3b82f6' }}>KP</div>
                </div>
                <span className="social-proof-text">Join others in the Network</span>
              </div>

              {/* Primary CTA */}
              <div className="viral-cta-wrap" ref={mainCtaRef}>
                <div className="viral-cta-glow" />
                <button onClick={handleStartTest} className="viral-cta-btn">
                  <Sparkles size={18} className="cta-sparkle-icon" />
                  Reveal Your Persona — Free
                  <ArrowRight size={16} />
                </button>
              </div>
              <p className="cta-microcopy">2 minutes · No account required to start</p>

              <button className="already-member-link" onClick={handleSignIn}>
                Already in the network? Sign in →
              </button>
            </div>
          )}

          {/* ── What you unlock ── */}
          {!user && (
            <div className="unlock-section invite-stagger-4">
              <p className="unlock-section-title">
                After 2 min, you and <strong>{inviterFirstName}</strong> both unlock:
              </p>
              <div className="unlock-features-grid">
                {[
                  { emoji: '💯', label: 'Compatibility Score' },
                  { emoji: '🤝', label: 'How You Click' },
                  { emoji: '⚡', label: 'Friction Points' },
                  { emoji: '💡', label: 'Tips to Get Along' },
                ].map((f, i) => (
                  <div key={i} className="unlock-feature-item">
                    <span className="unlock-feature-emoji">{f.emoji}</span>
                    <span>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── App explainer ── */}
          <div className="invite-app-explainer-wrapper invite-stagger-5">
            <div className="invite-app-explainer-glow" />
            <div className="invite-app-explainer">
              <div className="explainer-item">
                <div className="explainer-icon"><Brain size={16} /></div>
                <div className="explainer-text"><strong>AI Analysis</strong><span>Real science.</span></div>
              </div>
              <div className="explainer-item">
                <div className="explainer-icon"><Sparkles size={16} /></div>
                <div className="explainer-text"><strong>2 Min Quiz</strong><span>No login needed.</span></div>
              </div>
              <div className="explainer-item">
                <div className="explainer-icon"><Zap size={16} /></div>
                <div className="explainer-text"><strong>Compare</strong><span>See your match.</span></div>
              </div>
            </div>
          </div>

          {/* ── Privacy ── */}
          <div className="privacy-pill invite-stagger-5" style={{ marginTop: '1.5rem', marginBottom: '2rem' }}>
            <div className="pill-content">
              <Info size={12} />
              <span>Private & AI-Secured Comparison</span>
            </div>
          </div>

        </div>
      </div>

      {/* ── Sticky CTA bar — appears only after main CTA scrolls out of view ── */}
      {!user && showStickyBar && (
        <div className="sticky-invite-bar">
          <button onClick={handleStartTest} className="sticky-invite-btn">
            <Sparkles size={16} />
            Reveal Your Persona — Free
          </button>
        </div>
      )}
    </div>
  );
};
